'use client'

import { ArrowUpRight, MapPin, Phone } from 'lucide-react'
import { formatImageURL } from '@/utils/util'
import { getBranchOpenStatus, summariseWorkingHours } from '@/utils/branch'
import { hashIndex } from '@/utils/avatar'

const TINTS: string[] = [
  'linear-gradient(140deg, #C7DBFF 0%, #DCD9FF 100%)',
  'linear-gradient(140deg, #F6E8D1 0%, #EEDDC2 100%)',
  'linear-gradient(140deg, #D6F0E2 0%, #C5E9D8 100%)',
  'linear-gradient(140deg, #E8DEF7 0%, #D9CDEF 100%)',
  'linear-gradient(140deg, #EFE8DD 0%, #E1D6C3 100%)',
  'linear-gradient(140deg, #F8DDD1 0%, #F2C9B5 100%)',
  'linear-gradient(140deg, #F3D7E1 0%, #ECC4D2 100%)',
  'linear-gradient(140deg, #E6E7EA 0%, #D9DBE0 100%)',
]

export default function BranchCard({ branch, index = 0 }: { branch: any, vendor?: any, index?: number }) {
  const status = getBranchOpenStatus(branch?.workingHours)
  const hoursRows = summariseWorkingHours(branch?.workingHours)
  const tint = TINTS[hashIndex(String(branch?.id || branch?.name || ''), TINTS.length)]
  const num = String(index + 1).padStart(2, '0')

  const mapHref = branch?.latitude && branch?.longitude
    ? `https://www.google.com/maps/search/?api=1&query=${branch.latitude},${branch.longitude}`
    : branch?.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(branch.address)}` : null

  return (
    <a
      href={mapHref || '#'}
      target={mapHref ? '_blank' : undefined}
      rel={mapHref ? 'noopener noreferrer' : undefined}
      className="group bg-white rounded-3xl p-3.5 pb-5 flex flex-col gap-4 transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-12px_rgba(0,0,0,0.12)]"
    >
      <div className="relative aspect-4/3 rounded-2xl overflow-hidden" style={{ background: tint }}>
        {branch?.image && (
          <img src={formatImageURL(branch.image)} alt={branch?.name || ''} className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute top-3 left-3.5 text-[13px] font-semibold font-gilroy text-foreground/65">{num}</div>
        <div className={`absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white text-[12px] font-medium ${status.open ? 'text-foreground' : 'text-foreground/55'}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${status.open ? 'bg-emerald-500' : 'bg-foreground/30'}`} />
          {status.open ? 'Нээлттэй' : 'Хаалттай'}
        </div>
      </div>

      <div className="flex flex-col gap-1 px-1.5">
        <h3 className="text-[20px] sm:text-[22px] font-semibold font-gilroy tracking-[-0.012em] leading-tight text-foreground truncate">{branch.name}</h3>
        {branch.address && (
          <p className="mt-1.5 text-[14px] leading-[1.45] text-foreground/60 line-clamp-2">{branch.address}</p>
        )}
        {(() => {
          const phones = [branch.phone, ...(Array.isArray(branch.additionalPhones) ? branch.additionalPhones : [])].filter(Boolean)
          if (phones.length === 0) return null
          return (
            <div className="mt-2 flex items-start gap-1.5 text-[13px]">
              <Phone className="size-3.5 text-foreground/45 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                {phones.map((p, i) => (
                  <span key={i} className="inline-flex items-center gap-2">
                    <span
                      role="link"
                      tabIndex={0}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `tel:${p}` }}
                      className="font-medium text-foreground hover:underline cursor-pointer tabular-nums"
                    >
                      {p}
                    </span>
                    {i < phones.length - 1 && <span className="text-foreground/30">·</span>}
                  </span>
                ))}
              </div>
            </div>
          )
        })()}
      </div>

      <div className="mt-2 mx-1.5 pt-3.5 border-t border-dashed border-foreground/15 flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0 flex flex-col gap-1 text-[13px]">
          {hoursRows.length > 0 ? hoursRows.map((r, i) => (
            <div key={i} className="flex items-baseline justify-between gap-2.5">
              <span className="text-foreground/60 truncate">{r.day}</span>
              <span className="font-medium text-foreground">{r.time}</span>
            </div>
          )) : (
            <span className="font-medium text-foreground">{status.open ? 'Нээлттэй' : 'Хаалттай'}</span>
          )}
        </div>
        <span className="w-9 h-9 shrink-0 rounded-full bg-foreground text-cream inline-flex items-center justify-center transition-transform">
          {mapHref ? <MapPin className="size-4" /> : <ArrowUpRight className="size-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />}
        </span>
      </div>
    </a>
  )
}
